import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useGlobalAudio } from './GlobalAudioContext';
import { getTutorialFull, recordTutorialCompletion } from '../services/tutorialsService';
import { getStoredUser } from './AuthContext';
import { useLanguage } from './LanguageContext';

const TutorialContext = createContext(null);

function parseJson(str) {
    if (!str) return {};
    if (typeof str === 'object') return str;
    try { return JSON.parse(str); } catch { return {}; }
}

export const TutorialProvider = ({ children }) => {
    const { playAudio, stopAudio } = useGlobalAudio();
    const { language } = useLanguage();
    const [activeTutorial, setActiveTutorial] = useState(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const lang = language || 'fr';
    const navigateRef = useRef(null);

    // Appelé par TutorialOverlay au montage pour lui passer le useNavigate
    const registerNavigate = useCallback((navFn) => {
        navigateRef.current = navFn;
    }, []);

    const startTutorial = useCallback(async (uuid) => {
        stopAudio();
        const full = await getTutorialFull(uuid, lang);
        if (!full || !full.steps || full.steps.length === 0) {
            console.warn('[Tutorial] No steps in tutorial', uuid);
            return;
        }
        setActiveTutorial(full);
        setCurrentStepIndex(0);
    }, [lang, stopAudio]);

    const stopTutorial = useCallback((completed = false) => {
        stopAudio();
        if (activeTutorial && activeTutorial.uuid) {
            const user = getStoredUser();
            recordTutorialCompletion(activeTutorial.uuid, {
                userUuid: user?.peopleUuid || '',
                stepsCompleted: completed ? activeTutorial.steps.length : currentStepIndex,
                totalSteps: activeTutorial.steps.length,
                completed
            });
        }
        setActiveTutorial(null);
        setCurrentStepIndex(0);
    }, [activeTutorial, currentStepIndex, stopAudio]);

    const nextStep = useCallback(() => {
        if (!activeTutorial) return;
        stopAudio();
        const nextIndex = currentStepIndex + 1;
        if (nextIndex >= activeTutorial.steps.length) {
            stopTutorial(true);
        } else {
            setCurrentStepIndex(nextIndex);
        }
    }, [activeTutorial, currentStepIndex, stopAudio, stopTutorial]);

    const prevStep = useCallback(() => {
        if (!activeTutorial) return;
        stopAudio();
        setCurrentStepIndex(i => Math.max(0, i - 1));
    }, [activeTutorial, stopAudio]);

    const currentStep = activeTutorial && activeTutorial.steps ? activeTutorial.steps[currentStepIndex] : null;
    const currentStepLocalized = currentStep ? {
        ...currentStep,
        bubbleText: parseJson(currentStep.bubbletextbylang)[lang] || '',
        narrationText: parseJson(currentStep.narrationtextbylang)[lang] || '',
        audioPath: parseJson(currentStep.audiobylang)[lang] || ''
    } : null;

    const value = {
        activeTutorial,
        currentStep: currentStepLocalized,
        currentStepIndex,
        totalSteps: activeTutorial?.steps?.length || 0,
        lang,
        startTutorial,
        stopTutorial,
        nextStep,
        prevStep,
        registerNavigate,
        navigate: (path) => navigateRef.current && navigateRef.current(path),
        playStepAudio: playAudio
    };

    return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = () => {
    const ctx = useContext(TutorialContext);
    if (!ctx) throw new Error('useTutorial must be used within a TutorialProvider');
    return ctx;
};
